import IndividualStageInput from '../inputs/component-selection/individual-stage';
import SliderInput from '../inputs/component-selection/slider';
import Implementation from '../implementation/implementation';
import Optimization from '../optimization/optimization';
import FilterConstants from '../../filter-constants';
import {
  generateCapacitorRange,
  generateGainRange,
  getMaximumValueIndex,
  getMinimumValueIndex,
  getValueIndex,
} from '../utils/slider';
import { computeCompensateLoop } from '../../utils/common';
import MathUtils from '../utils/math';
import Series from '../tolerances/series';
import SeriesPrecision from '../tolerances/series-precision';
import SecondOrder from '../stages/second-order';
import FirstOrder from '../stages/first-order';
import Capacitor from '../components/capacitor';
import ToleranceInput from '../inputs/tolerance';
import ActualSecondOrder from '../stages/actual-second-order';
import ActualFirstOrder from '../stages/actual-first-order';
import SecondOrderTargetStage from '../stages/second-order';
import IndexModel from '..';
import SallenKeyLowPassCircuit from '../implementation/sallen-key/low-pass/circuit';
import TargetStage from '../stages/target-stage';
import C1 from '../../models/components/c1';
import R3 from '../../models/components/r3';
import FirstOrderLowPassCircuit from '../implementation/first-order/low-pass/circuit';
import InductorBandPass from '../implementation/inductor/band-pass/circuit';
import InductorHighPassCircuit from '../implementation/inductor/high-pass/circuit';
import SallenKeyHighPassCircuit from '../implementation/sallen-key/high-pass/circuit';
import MultipleFeedbackHighPassCircuit from '../implementation/multiple-feedback/high-pass/circuit';
import MultipleFeedbackLowPassCircuit from '../implementation/multiple-feedback/low-pass/circuit';

const _defaults = {
  optimization: FilterConstants.optimizationType.power,
};

export type Circuit =
  | (SallenKeyLowPassCircuit & { c2?: unknown & { value: number } } & { c5?: unknown & { value: number } } & {
      r5?: unknown & { value: number };
    } & { k?: unknown & { value: number } })
  | (FirstOrderLowPassCircuit & { c2?: unknown & { value: number } } & { c5?: unknown & { value: number } } & {
      r2?: unknown & { value: number };
    } & { r5?: unknown & { value: number } } & { k?: unknown & { value: number } })
  | (InductorBandPass & { c2?: unknown & { value: number } } & { c5?: unknown & { value: number } } & {
      r2?: unknown & { value: number };
    } & { r5?: unknown & { value: number } } & { k?: unknown & { value: number } })
  | (InductorHighPassCircuit & { c2?: unknown & { value: number } } & { c5?: unknown & { value: number } } & {
      r2?: unknown & { value: number };
    } & { r5?: unknown & { value: number } } & { k?: unknown & { value: number } })
  | (SallenKeyHighPassCircuit & { c5?: unknown & { value: number } } & { r5?: unknown & { value: number } } & {
      k?: unknown & { value: number };
    })
  | (MultipleFeedbackHighPassCircuit & { r3?: unknown & { value: number } } & { r4?: unknown & { value: number } } & {
      r5?: unknown & { value: number };
    } & { k?: unknown & { value: number } })
  | (MultipleFeedbackLowPassCircuit & { c5?: unknown & { value: number } } & { r3?: unknown & { value: number } } & {
      r4?: unknown & { value: number };
    } & { k?: unknown & { value: number } });

export type StageInput = IndividualStageInput & {
  legacyGainSliderValue: string;
  legacySizingSliderValue: string;
  compensateForGbw: boolean;
};

type C1Object = C1 & {
  capacitorMaximumLimitMultiplier: number;
};

////////////////////////////////////////////////////////////////////////////////////
// individual-stage - Advisor for stage inputs when user enters components tab    //
////////////////////////////////////////////////////////////////////////////////////

export default class IndividualStageAdvisor {
  // model = null; // takes in the domain model
  model: IndexModel;

  constructor(model: IndexModel) {
    this.model = model;
  }
  // Creates the individual stages input model
  // Inputs:
  //   - data : input model values, if this is null, defaults are used
  // Returns: individual stages input model
  generate(
    stage: SecondOrderTargetStage,
    data: IndividualStageInput | null,
    isOldDesign: boolean | null,
    legacySizingSliderValue: number | null,
    legacyGainSliderValue: number | null,
  ): IndividualStageInput {
    return new IndividualStageInput(
      data ? data : this.getDefaults(stage, isOldDesign, legacySizingSliderValue, legacyGainSliderValue),
    );
  }

  // Returns the default values for a stage given a specific implementation
  // Inputs:
  //   - stage : target stage information
  //   - implementationInput : stage implementation
  //   - stageInput : individual stage input
  // Returns: default values of the individual stage input based on target stage, implementation, and other stage input information
  getImplementationDefaults(
    stage: SecondOrderTargetStage,
    implementationInput: string,
    stageInput?: StageInput,
  ): IndividualStageInput {
    const implementationObj = Implementation.create({
      selectedImplementation: implementationInput,
      filter: stage.filter,
    });

    return this._getStageDefaults(stage, implementationObj, stageInput);
  }

  // Returns the default values for a stage
  // Inputs:
  //   - stage : stage information
  //   - isOldDesign: flag that checks if the design came from an older filter wizard application
  //   - legacySizingSliderValue : value of the component sizing slider for older designs
  //   - legacyGainSliderValue : value of the gain slider for older designs
  // Returns: stage input default values
  getDefaults(
    stage: SecondOrderTargetStage,
    isOldDesign: boolean | null,
    legacySizingSliderValue: number | null,
    legacyGainSliderValue: number | null,
  ): IndividualStageInput {
    const implementationObj = Implementation.create({
      selectedImplementation: stage.defaultImplementation,
      filter: stage.filter,
    });

    const stageDefaults = this._getStageDefaults(stage, implementationObj);

    if (isOldDesign) {
      const circuit = this._getCircuit(stage, implementationObj);
      stageDefaults.componentSizing.value = this._getSliderValueFromLegacy(circuit.c1, legacySizingSliderValue);
      stageDefaults.gainRatio.value = this._getGainSliderValueFromLegacy(circuit.r3, legacyGainSliderValue);
    }

    return stageDefaults;
  }

  // Creates a circuit based on target stage, implementation and stage input information
  // Inputs:
  //   - stage : target stage information
  //   - implementationObj: implementation object
  //   - stageInput : individual stage input
  // Returns: actual stage object
  _getCircuit(stage: SecondOrderTargetStage, implementationObj: Implementation, stageInput?: StageInput): Circuit {
    const optimization = Optimization.create({ optimization: _defaults.optimization });
    const circuit = implementationObj.createCircuit(stage.stageFp, stage.gain, stage.stageQ, optimization) as Circuit;
    let masterCapacitorValue = circuit.masterCapacitor.value;
    let r3Value = circuit.r3 ? circuit.r3.value : -1;
    let opAmp = FilterConstants.idealOpAmp;
    if (stageInput) {
      const c1Index = stageInput.componentSizing.value;
      let r3Index = stageInput.gainRatio.value;
      const capacitorValues = generateCapacitorRange(Series.eSeries24);
      const gainValues = generateGainRange(Series.eSeries96);
      if (!!circuit.r3 && !!circuit.r3.value && r3Index === -1) {
        const r3Maximum = circuit.r3.maximum;
        const r3Minimum = circuit.r3.minimum;
        const r3Value = circuit.r3.value;
        r3Index = getValueIndex(gainValues, r3Minimum, r3Maximum, r3Value);
      }

      masterCapacitorValue = capacitorValues[c1Index];
      r3Value = gainValues[r3Index];
      opAmp = stageInput.opAmp;
    }

    const opAmpSource = this.model.opAmpSource;
    const componentsInput = this.model.inputs.components;
    const stopbandFrequency = this.model.inputs.specifications.stopbandFrequency;
    const compensateForGbw = stageInput ? stageInput.compensateForGbw : false;
    const compensateLoop = computeCompensateLoop(compensateForGbw);

    const actualStagesPropertiesHash = {
      targetStage: stage,
      selectedImplementation: implementationObj.selectedImplementation,
      compensateLoop: compensateLoop,
      opAmp: opAmp,
      optimization: optimization,
      masterCapacitorValue: masterCapacitorValue,
      r3Value: r3Value,
      stopbandFrequency: stopbandFrequency,
      componentsInput: componentsInput,
      opAmpSource: opAmpSource,
    };

    let res = null;
    if (stage instanceof FirstOrder) {
      res = ActualFirstOrder.create(actualStagesPropertiesHash);
    } else {
      res = ActualSecondOrder.create(actualStagesPropertiesHash);
    }

    return res.circuit;
  }

  // Returns the default values for a stage
  // Inputs:
  //   - stage : target stage information
  //   - implementationObj: implementation object
  //   - stageInput : individual stage input
  // Returns: default value for the individual stage input
  _getStageDefaults(
    stage: SecondOrderTargetStage,
    implementationObj: Implementation,
    stageInput?: StageInput,
  ): IndividualStageInput {
    const circuit = this._getCircuit(stage, implementationObj, stageInput);
    const componentSizing = this.getComponentSizingValues(circuit);
    const gainRatio = this.getGainRatio(circuit);

    return {
      stageLetter: null,
      implementation: implementationObj.selectedImplementation,
      componentSizing: componentSizing,
      gainRatio: gainRatio,
      opAmp: FilterConstants.idealOpAmp,
      implementationSelection: this._getImplementationSelection(stage, implementationObj.selectedImplementation),
    };
  }

  // Creates a Copy of the Individual Stage Input Model
  // Inputs:
  //   - sourceStage : model to be copied from
  // Returns: result - a newly created individual stage input model with values copied from the source model
  clone(sourceStage: StageInput): IndividualStageInput {
    const result = new IndividualStageInput() as StageInput;
    result.implementation = sourceStage.implementation;
    result.componentSizing = this._cloneSlider(sourceStage.componentSizing);
    result.gainRatio = this._cloneSlider(sourceStage.gainRatio);
    result.opAmp = sourceStage.opAmp;
    result.implementationSelection = { selections: [], selectedItem: null };
    for (let i = 0; i < sourceStage.implementationSelection.selections.length; i++) {
      result.implementationSelection.selections.push(sourceStage.implementationSelection.selections[i]);
    }
    result.implementationSelection.selectedItem = sourceStage.implementationSelection.selectedItem;
    result.legacyGainSliderValue = sourceStage.legacyGainSliderValue;
    result.legacySizingSliderValue = sourceStage.legacySizingSliderValue;
    result.stageLetter = sourceStage.stageLetter;
    return result;
  }

  // Creates a Copy of the Individual Slider Model
  // Inputs:
  //  - sourceSlider - model to be copied from
  // Returns: result - a newly created input model with values copied from the source model
  _cloneSlider(sourceSlider: SliderInput): SliderInput {
    const result = new SliderInput();
    result.enabled = sourceSlider.enabled;
    result.minimum = sourceSlider.minimum;
    result.maximum = sourceSlider.maximum;
    result.value = sourceSlider.value;
    return result;
  }

  // Creates a component sizing object with minimum, maximum and value
  // Inputs:
  //  - circuit - stage circuit data
  // Returns: result - a slider object containing minimum, maximum and selected value, as well as if the slider is enabled
  getComponentSizingValues(circuit: Circuit): SliderInput {
    const sliderValues = generateCapacitorRange(Series.eSeries24);
    const maximumIndex = getMaximumValueIndex(sliderValues, circuit.masterCapacitor.maximum, SeriesPrecision.eSeries24);
    const minimumIndex = getMinimumValueIndex(sliderValues, circuit.masterCapacitor.minimum, SeriesPrecision.eSeries24);
    const sliderValueIndex = getValueIndex(
      sliderValues,
      circuit.masterCapacitor.minimum,
      circuit.masterCapacitor.maximum,
      circuit.masterCapacitor.value,
      SeriesPrecision.eSeries24,
    );

    const result = new SliderInput({
      enabled: true,
      maximum: maximumIndex,
      minimum: minimumIndex,
      value: sliderValueIndex,
    });

    return result;
  }

  // Creates a gain ratio object with minimum, maximum and value
  // Inputs:
  //  - circuit - stage circuit data
  // Returns: result - a slider object containing minimum, maximum and selected value, as well as if the slider is enabled
  getGainRatio(circuit: Circuit): SliderInput {
    const tolerances = this.model.inputs.tolerances;
    const allowGainChange = circuit.allowGainChange;
    const k = circuit.k ? circuit.k.value : null;
    // in determining gain resistors, gain is formulated as 1 / k
    const gain = 1 / (k as number);
    const maximum = circuit.r3 ? circuit.r3.maximum : null;
    const minimumR3 = circuit.r3 ? circuit.r3.minimum : null;
    // in components-factory > createR4FromR3AndGain function, r4IdealValue is formulated as:
    // let r4IdealValue = r3Value * (gain - 1)
    // minimum resistance for ideal, 1%, 5%, and 10% tolerance is 1 and 10 for 0.1% and 0.5%
    // so substituting it to r4IdealValue produces below expression where minimumIdealR3 is the r3Value
    let minimumIdealR3 = FilterConstants.Limits.RSMALL_COMP_LIMIT / (gain - 1);
    if (tolerances && (tolerances.resistorTolerance === '0.1%' || tolerances.resistorTolerance === '0.5%')) {
      minimumIdealR3 = 10 / (gain - 1);
    }
    const minimum = Math.max(minimumR3 as number, minimumIdealR3);

    let value = circuit.r3 ? circuit.r3.value : null;
    if (allowGainChange) {
      if (value > maximum) {
        value = maximum;
      }
      if (value < minimum) {
        value = minimum;
      }
    }

    const series = Series.eSeries96;
    const sliderValues = generateGainRange(series);
    const maximumIndex = getMaximumValueIndex(sliderValues, maximum, SeriesPrecision.eSeries96);
    const minimumIndex = getMinimumValueIndex(sliderValues, minimum, SeriesPrecision.eSeries96);
    const sliderValueIndex = getValueIndex(sliderValues, minimum, maximum, value, SeriesPrecision.eSeries96);

    const result = new SliderInput({
      enabled: !!allowGainChange,
      maximum: maximumIndex,
      minimum: minimumIndex,
      value: sliderValueIndex,
    });

    return result;
  }

  // Creates a implementation object with possible implementations and selected one
  // Inputs:
  //  - targetStage - target stage data
  //  - selectedImplementation - selected implementation
  // Returns: result - implementation object containing a list of implementations and the selected implementation
  _getImplementationSelection(
    targetStage: TargetStage,
    selectedImplementation: string,
  ): { selections: string[]; selectedItem: string } {
    const filterName = targetStage.filter.name;
    const result: { selections: string[]; selectedItem: string } = { selections: [], selectedItem: '' };
    if (
      targetStage instanceof SecondOrder &&
      (filterName === FilterConstants.filterType.lowPass || filterName === FilterConstants.filterType.highPass) &&
      selectedImplementation !== FilterConstants.implementationType.inductor
    ) {
      result.selections.push(FilterConstants.implementationType.sallenKey);
      result.selections.push(FilterConstants.implementationType.multipleFeedback);
      result.selectedItem = selectedImplementation;
    } else {
      result.selections = [selectedImplementation];
      result.selectedItem = selectedImplementation;
    }
    return result;
  }

  // Computes for the component sizing slider value based on legacy filter wizard designs
  // Inputs:
  //  - c1 - c1 object
  //  - sliderValue - legacy slider value
  // Returns: sliderValueIndex - computed component sizing slider value
  _getSliderValueFromLegacy(c1: C1Object, sliderValue: number | null): number {
    const toleranceInput = new ToleranceInput({
      capacitorTolerance: FilterConstants.capacitorTolerance.one,
      capacitorPreferredSeries: 'E24',
    });
    const c1New = Capacitor.create({
      idealValue: c1.idealValue,
      capacitorMinimumLimitMultiplier: c1.capacitorMinimumLimitMultiplier,
      capacitorMaximumLimitMultiplier: c1.capacitorMaximumLimitMultiplier,
      rcRatio: sliderValue,
    });

    return getValueIndex(
      generateCapacitorRange(Series.eSeries24),
      c1.minimum,
      c1.maximum,
      c1New.toCommercialComponent(toleranceInput).getNominalValue(),
    );
  }

  // Computes for the gain ratio slider value based on legacy filter wizard designs
  // Inputs:
  //  - r3 - r3 object
  //  - sliderValue - legacy slider value
  // Returns: sliderValueIndex - computed gain ratio sizing slider value
  _getGainSliderValueFromLegacy(r3: R3, sliderValue: number | null): number {
    const value = r3.minimum * Math.pow(10, MathUtils.logBaseTen(r3.maximum / r3.minimum) * (1 - (sliderValue ?? 0)));
    return getValueIndex(generateGainRange(Series.eSeries96), r3.minimum, r3.maximum, value);
  }
}
